<template>
  <div :class="{ 'dark-mode-item': true }">
    <Icon
      class="dark-mode-item__icon"
      name="duotone-darkmode"
    />
    <div class="dark-mode-item__text">Dark Mode</div>
    <Badge
      label="Beta"
      :color="backgroundColor"
      size="medium"
      :override-label-color="text"
      class="dark-mode-item__badge"
      no-tooltip
    />
    <Toggle
      :model-value="darkModeEnabled"
      kind="primary"
      size="small"
      @update:model-value="toggleDarkMode"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import Badge from '@/uiKit/Badge/Badge.vue'
import { hslaToRGBA } from '@/uiKit/colorPalette'

type Appearance = 'light' | 'dark'

const darkModeEnabled = ref(window.localStorage.getItem('appearance') === 'dark')

const persistAppearance = (appearance: Appearance): void => {
  window.localStorage.setItem('appearance', appearance)
}

const toggleDarkMode = (): void => {
  const updatedDarkModeEnabled = !darkModeEnabled.value
  const updatedAppearance = updatedDarkModeEnabled ? 'dark' : 'light'

  darkModeEnabled.value = updatedDarkModeEnabled

  persistAppearance(updatedAppearance)
  document.documentElement.setAttribute('data-theme', updatedAppearance)
}

// use light mode version for now
// $orange-alpha-100: hsl(34 100% 52% / 0.17);
const backgroundColor = hslaToRGBA({
  h: 34,
  s: 100,
  l: 52,
  a: 0.17,
})

const text = getComputedStyle(document.body).getPropertyValue('--color-text-warning')
</script>

<style lang="scss" scoped>
.dark-mode-item {
  @include row;
  align-items: center;
  padding: 6px 6px;
  border-radius: 6px;

  &:hover {
    background-color: var(--color-background-transparent-hovered);
  }

  &__badge {
    margin-right: auto;
    margin-left: 2px;
  }

  &__icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    color: var(--color-icon-subtle);
  }

  &__text {
    @include typography(md-1, headlines);
    @include typographyRegularBody200;
    color: var(--color-text-default);
  }
}
</style>
